import { Icon, Stack, Tooltip, Typography } from '@mui/material';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { ArchiveIcon } from '@schooly/style';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { DropdownSelect, DropdownSelectProps } from '../DropdownSelect';
import { DropdownSelectRow } from '../DropdownSelectRow';
import { SelectContentSkeleton } from '../SelectContentSkeleton';
import { SelectOptionsArchivedIcon } from '../SelectOptionsArchivedIcon';
import { SelectSearchInput } from '../SelectSearchInput';

type AgeGroupSelectProps = {
  userRole: SchoolUserRole;
  schoolId: string;
  selectedId: string | undefined;
  onSelect: (id: string | undefined) => void;
  isAdmin?: boolean;
  hideIfNoOptions?: boolean;
  shouldOpen?: boolean;
} & Omit<DropdownSelectProps, 'children' | 'renderContent'>;

export const AgeGroupSelect: FC<AgeGroupSelectProps> = ({
  schoolId,
  selectedId,
  userRole,
  placeholder,
  isAdmin,
  onSelect,
  hideIfNoOptions,
  disabled,
  shouldOpen,
  ...dropdownProps
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdown = useRef<DropdownSelect | null>(null);
  const [query, setQuery] = useState('');
  const { formatMessage } = useIntl();
  const { getConfirmation } = useConfirmationDialog();

  const [queryEnabled, setQueryEnabled] = useState(false);

  const {
    ageGroups,
    activeAgeGroups: activeAgeGroupsInit,
    archivedAgeGroups: archivedAgeGroupsInit,
    getAgeGroupById,
    isLoading,
  } = useAgeGroups({ schoolId, userType: userRole }, { enabled: queryEnabled || !!selectedId });

  const selectedAgeGroup = selectedId ? getAgeGroupById(selectedId) : undefined;
  const isSelectedArchived = !!selectedAgeGroup?.archived;

  const showAllArchivedRightIcon =
    !selectedId && !activeAgeGroupsInit?.length && archivedAgeGroupsInit?.length;

  const renderAllArchivedIcon = useCallback(
    () => <SelectOptionsArchivedIcon isAdmin={!!isAdmin} type={SchoolPropertyType.AgeGroup} />,
    [isAdmin],
  );

  const getArchivedConfirmation = useCallback(async () => {
    return await getConfirmation({
      textId: `deselect-property-archived-${SchoolPropertyType.AgeGroup}`,
      textValues: { name: selectedAgeGroup ? selectedAgeGroup.name : '' },
    });
  }, [selectedAgeGroup, getConfirmation]);

  const handleClear = useCallback(async () => {
    if (isSelectedArchived) {
      const confirmed = await getArchivedConfirmation();
      if (!confirmed) return;
    }

    onSelect(undefined);
    setQuery('');
    dropdown.current?.close();
  }, [isSelectedArchived, onSelect, getArchivedConfirmation]);

  const handleSelect = useCallback(
    async (id: string) => {
      if (isSelectedArchived) {
        const confirmed = await getArchivedConfirmation();
        if (!confirmed) return;
      }

      onSelect(id);
      setQuery('');
      dropdown.current?.close();
    },
    [onSelect, isSelectedArchived, getArchivedConfirmation],
  );

  const handleToggle = useCallback((isOpened: boolean) => {
    setQueryEnabled(isOpened);
    setQuery('');
  }, []);

  useEffect(() => {
    if (shouldOpen) {
      dropdown.current?.open();
    }
  }, [shouldOpen]);

  const renderContent = useCallback(() => {
    if (isLoading) return <SelectContentSkeleton />;

    const activeAgeGroups = activeAgeGroupsInit?.filter((p) =>
      p.name.toLowerCase().includes(query.toLowerCase()),
    );

    if (!activeAgeGroups?.length)
      return (
        <Typography p={1}>
          <FormattedMessage id="input-NoOptionsFound" />
        </Typography>
      );

    return (
      <Stack gap={0.25} p={1}>
        {activeAgeGroups?.map((ageGroup) => (
          <DropdownSelectRow
            key={ageGroup.id}
            isSelected={selectedId === ageGroup.id}
            onClick={() => handleSelect(ageGroup.id)}
          >
            {ageGroup.name}
          </DropdownSelectRow>
        ))}
      </Stack>
    );
  }, [isLoading, activeAgeGroupsInit, query, selectedId, handleSelect]);

  const renderSelectedAgeGroup = useCallback(() => {
    if (!selectedAgeGroup || query) return null;

    return (
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={{
          maxWidth: '100%',
          pointerEvents: 'none',
          color: disabled ? 'common.grey2' : 'primary.main',
        }}
      >
        {isSelectedArchived && (
          <Tooltip
            title={formatMessage({
              id: `schoolProperty-Archived-${SchoolPropertyType.AgeGroup}`,
            })}
          >
            <Icon sx={{ pointerEvents: 'auto', zIndex: 1 }}>
              <ArchiveIcon />
            </Icon>
          </Tooltip>
        )}
        <Typography variant="h3" color="inherit" noWrap>
          {selectedAgeGroup.name}
        </Typography>
      </Stack>
    );
  }, [selectedAgeGroup, isSelectedArchived, formatMessage, query, disabled]);

  if (hideIfNoOptions && !isLoading && !ageGroups.length) return null;

  return (
    <DropdownSelect
      {...dropdownProps}
      disabled={disabled}
      ref={dropdown}
      onToggle={handleToggle}
      placeholder={placeholder}
      hasValues={!!selectedId}
      renderContent={renderContent}
      renderRightIcon={showAllArchivedRightIcon ? renderAllArchivedIcon : undefined}
      onClickInputArea={() => inputRef.current?.focus()}
      onClear={selectedId ? handleClear : undefined}
    >
      {(opened) => (
        <>
          {renderSelectedAgeGroup()}
          {opened && (
            <SelectSearchInput
              ref={inputRef}
              autoFocus
              value={query || ''}
              onChangeText={setQuery}
              debounced={false}
              sx={(theme) => ({
                ...theme.typography.h3,
                position: 'absolute',
                left: theme.spacing(1.5),
                right: theme.spacing(5),
              })}
            />
          )}
        </>
      )}
    </DropdownSelect>
  );
};
